import { inject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

@inject(I18N)
export class GoogleMapsService {
    constructor(i18n) {
        this.i18n = i18n;
        this.maps = null;
        this.map = null;
        this.featureLayer = null;
        this.placeService = null;
        this.geocoderService = null;
        this.infoWindow = null;
        this.apiKey = null;
        this.mapId = null;
        this.language = 'de';
        this.mapCircle = null;
        this.eventLocationMarkers = [];
    }

    loadGoogleMaps(apiKey, libraries = ['places', 'marker']) {
        if (this.isLoaded) {
            return Promise.resolve();
        }

        if (this.loadPromise) {
            return this.loadPromise;
        }

        this.loadPromise = new Promise((resolve, reject) => {
            if (typeof google !== 'undefined' && google.maps) {
                this.isLoaded = true;
                resolve();
                return;
            }

            const script = document.createElement('script');
            const librariesParam =
                libraries.length > 0 ? `&libraries=${libraries.join(',')}` : '';
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}${librariesParam}`;
            script.async = true;
            script.defer = true;

            script.onload = () => {
                this.isLoaded = true;
                resolve();
            };

            script.onerror = () => {
                reject(new Error('Failed to load Google Maps API'));
            };

            document.head.appendChild(script);
        });

        return this.loadPromise;
    }

    async initialize(apiKey, mapId, language, applyStyleCallback) {
        this.apiKey = apiKey;
        this.mapId = mapId;
        this.language = language;

        if (!this.apiKey || !this.mapId) {
            console.error('Google Maps API key or Map ID is missing!');
            return;
        }
        await this.loadGoogleMaps(this.apiKey, ['places', 'marker']);
        this.maps = google.maps;
        if (!this.maps || typeof this.maps.Map !== 'function') {
            console.error('Google Maps API failed to load properly.');
            return;
        }

        const mapElement = document.getElementById('planning-map');
        const inputElement = document.getElementById('city-search');

        if (!mapElement || !inputElement) {
            console.error('Map or input element not found!');
            return;
        }

        const startCoords = { lat: 50.9375, lng: 6.9603 };
        this.map = this.createMap(mapElement, startCoords);

        this.placeService = new this.maps.places.PlacesService(
            document.createElement('div')
        );
        this.geocoderService = new this.maps.Geocoder();
        this.infoWindow = new this.maps.InfoWindow({});

        this.setupAutocomplete(inputElement);
        await this.initFeatureLayer(applyStyleCallback);
    }

    createMap(mapElement, startCoords) {
        return new google.maps.Map(mapElement, {
            center: startCoords,
            zoom: 12,
            mapId: this.mapId,
            mapTypeControl: false,
            streetViewControl: false,
        });
    }

    setupAutocomplete(inputElement) {
        const autocomplete = new google.maps.places.Autocomplete(inputElement, {
            types: ['geocode'],
            componentRestrictions: { country: 'DE' },
        });

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            this.zoomToPlace(place);
        });
    }

    async initFeatureLayer(applyStyleCallback) {
        this.featureLayer = this.map.getFeatureLayer('POSTAL_CODE');
        if (!this.featureLayer) {
            console.error('Feature Layer could not be initialized.');
            return;
        }

        this.featureLayer.addListener('click', async (event) => {
            const hoveredPlaceIds = event.features.map((f) => f.placeId);
            applyStyleCallback('click', hoveredPlaceIds, event);
        });

        this.featureLayer.addListener('mousemove', (event) => {
            const hoveredPlaceIds = event.features.map((f) => f.placeId);
            applyStyleCallback('mousemove', hoveredPlaceIds, event);
        });

        this.map.addListener('mousemove', () => {
            applyStyleCallback('mouseout');
        });

        this.setFeatureLayerStyle(applyStyleCallback);
    }

    setFeatureLayerStyle(styleCallback) {
        this.featureLayer.style = styleCallback;
    }

    zoomToPlace(place) {
        if (!place?.geometry) {
            console.error('No details available for input.');
            return;
        }
        this.map.setCenter(place.geometry.location);
        this.map.setZoom(12);
    }

    fitBounds(bounds) {
        var googleBounds = new this.maps.LatLngBounds(
            new this.maps.LatLng(bounds.southwest.lat, bounds.southwest.lng),
            new this.maps.LatLng(bounds.northeast.lat, bounds.northeast.lng),
        );

        this.map.fitBounds(googleBounds);
    }

    placeDetails(placeId) {
        return new Promise((resolve, reject) => {
            this.placeService.getDetails({ placeId }, (results, status) => {
                if (status === this.maps.places.PlacesServiceStatus.OK) {
                    resolve(results);
                } else {
                    reject(status);
                }
            });
        });
    }

    addAdvancedMarkerToMap(location, onMarkerClick, onMarkerHover) {
        if (!location?.address?.coordinates) {
            console.error('Invalid location data for marker.');
            return;
        }

        const pinBackground = new this.maps.marker.PinElement({
            background: location.color,
            glyphColor: location.borderColor,
            borderColor: location.borderColor,
        });

        const marker = new this.maps.marker.AdvancedMarkerElement({
            map: this.map,
            position: location.address.coordinates,
            content: pinBackground.element,
            gmpClickable: true,
        });

        if (onMarkerClick) {
            marker.addListener('click', () => {
                onMarkerClick(location);
            });
        }

        if (onMarkerHover) {
            marker.content.getRootNode().addEventListener('mouseenter', function () {
                onMarkerHover(location, true);
            });

            marker.content.getRootNode().addEventListener('mouseleave', function () {
                onMarkerHover(location, false);
            });
        }

        this.eventLocationMarkers.push(marker);
    }

    addMarkerToMap(location, onMarkerClick, markerIcon, clickAble = true) {
        if (!location?.address?.coordinates) {
            console.error('Invalid location data for marker.');
            return;
        }

        markerIcon = markerIcon || {
            url:
                'https://humboldt-tzcd-file.s.io/dcc21af3-abbf-49a6-a4bd-6cdc6aa83b12/home-pin.png',
        };

        const marker = new this.maps.Marker({
            position: location.address.coordinates,
            map: this.map,
            icon: markerIcon,
        });

        const infoContent = this.createMarkerTemplate(location);
        if (clickAble) {
            marker.addListener('click', () => {
                this.infoWindow.setContent(infoContent);
                this.infoWindow.open(this.map, marker);
                onMarkerClick(location);
            });
        }

        this.eventLocationMarkers.push(marker);
        return marker;
    }

    clearMarkers() {
        this.eventLocationMarkers.forEach((marker) => marker.setMap(null));
        this.eventLocationMarkers = [];
    }

    createMarkerTemplate(location) {
        const title = location.title?.de || location.title;
        const { addressLine1, city, zip, country } = location.address;
        const address = `${addressLine1}, ${city}, ${zip}, ${country}`;
        const phone = location?.phones?.[0]?.phone || 'N/A';
        const url = `/view/tourism-event/location?bulk=false&id=${location.id}&modelId=tourism-event%2Flocation`;
        const addressLabel = this.i18n.tr('sio.field.address');
        const phoneLabel = this.i18n.tr('sio.field.phone');
        const submitLabel = this.i18n.tr('tourism-order.select');

        const image = location?.images?.[0]?.file?.previewUrl
            ?.replace('$width', '300')
            .replace('$height', '300');
        const imageHtml = image
            ? `<img src="${image}" alt="Parking Image" />`
            : '';

        return `
        <div class='custom-info'>
            ${imageHtml}
            <div class='info-content'>
                <a href='${url}' target='_blank'>${title}</a><br/>
                <span><strong>${addressLabel}:</strong> ${address}</span><br/>
                <span><strong>${phoneLabel}:</strong> ${phone}</span><br/>
                <button class='select-location-btn select-button' data-id='${location.id}'>${submitLabel}</button>
            </div>
        </div>
    `;

    }

    drawCircle(center, radiusInKm, onRadiusChange) {
        if (this.mapCircle) {
            this.mapCircle.setMap(null);
        }

        this.mapCircle = new this.maps.Circle({
            center,
            radius: radiusInKm * 1000,
            map: this.map,
            strokeColor: '#0000FF',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#0000FF',
            fillOpacity: 0.10,
            editable: true,
            draggable: false,
            clickable: false,
        });

        this.maps.event.addListener(this.mapCircle, 'radius_changed', () => {
            const newRadius = this.mapCircle.getRadius() / 1000;
            onRadiusChange(newRadius);
        });
    }

    reverseGeocode(location) {
        return new Promise((resolve, reject) => {
            this.geocoderService.geocode({ location }, (results, status) => {
                if (status === 'OK' && results.length > 0) {
                    const postalCodeComponent = results.find((res) =>
                        res.types.includes('postal_code')
                    );
                    if (postalCodeComponent) {
                        const placeId = results[0].place_id;
                        resolve({
                            postalCode:
                                postalCodeComponent.address_components[0]
                                    .long_name,
                            placeId,
                        });
                    } else {
                        reject(null);
                    }
                } else {
                    reject(null);
                }
            });
        });
    }

    getRectForPlaceId(placeId) {
        return new Promise((resolve, reject) => {
            this.geocoderService.geocode(
                {
                    placeId: placeId,
                },
                (results, status) => {
                    if (status === 'OK' && results[0]) {
                        resolve(results[0].geometry.bounds);
                    } else {
                        reject(null);
                    }
                }
            );
        });
    }
}
